import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { Box, FormControlLabel, Radio, RadioGroup, Typography, useTheme } from '@mui/material';

// Components
import { ErrorBoundary } from 'common';

// Utils
import { formatNumber } from 'utils/helpers/format';
import { theme } from 'library';
import { INVENTORY_SORT_ORDER } from '../../../utils';

// Styles
import { radioButtonStyles, radioGroupStyles } from '../Fulfillment.styles';

const findSelectedOption = (allOptions, reconsignmentControl, inventorySelection, isFutureDate) => {
  const selectedOption = allOptions.find(
    option =>
      option.value.reconsignmentControl === reconsignmentControl &&
      option.value.inventorySelection === inventorySelection
  );

  // If selected option has zero balance, find first non-zero option
  if (selectedOption?.value.balance === 0 && !isFutureDate) {
    const nonZeroOption = allOptions.find(option => option.value.balance > 0);
    if (nonZeroOption) return nonZeroOption;
  }

  const defaultOption = allOptions.find(
    option =>
      option.value.reconsignmentControl === reconsignmentControl &&
      option.value.inventorySelection === 'EITHER'
  );

  return selectedOption || defaultOption;
};

const RadioLabel = ({ option, isFutureDate }) => {
  const hasString = str => option.key.toLowerCase().includes(str);

  const labelText = hasString('any') ? 'Any' : option.title.split(' ')[1];
  const renderLabel = (variant, text, balance) => {
    return (
      <>
        <Typography noWrap sx={{ flex: 1, minWidth: 0 }} variant="body1">
          {!text ? 'Any ' : ''}
          {variant} {text}
        </Typography>
        {!isFutureDate && (
          <Typography sx={{ flexShrink: 0 }} variant="body1">{`${formatNumber(
            balance
          )} bbls`}</Typography>
        )}
      </>
    );
  };

  switch (true) {
    case hasString('uncontrolled') && labelText !== 'Any':
      return renderLabel('Uncontrolled', labelText, option.balance);
    case hasString('uncontrolled') && labelText === 'Any':
      return renderLabel('Uncontrolled', '', option.balance);

    case hasString('controlled') && labelText !== 'Any':
      return renderLabel('Controlled', labelText, option.balance);
    case hasString('controlled') && labelText === 'Any':
      return renderLabel(labelText, 'Controlled', option.balance);

    case !hasString('controlled') && !hasString('uncontrolled') && !hasString('inventory'):
      return renderLabel('Any', option.title.split(' ')[1], option.balance);

    default:
      return (
        <>
          <Typography noWrap sx={{ flex: 1, minWidth: 0 }} variant="body1">
            Any Inventory
          </Typography>
          {!isFutureDate && (
            <Typography sx={{ flexShrink: 0 }} variant="body1">{`${formatNumber(
              option.balance
            )} bbls`}</Typography>
          )}
        </>
      );
  }
};

RadioLabel.propTypes = {
  isFutureDate: bool.isRequired,
  option: shape({
    balance: number,
    key: string,
    title: string,
  }).isRequired,
};

const PillsSection = ({
  inventorySelection,
  inventoryOptions,
  isFutureDate,
  setValue,
  restrictedFlow,
  reconsignmentControl,
  volume,
}) => {
  const { palette } = useTheme();

  const selectedOption = findSelectedOption(
    inventoryOptions,
    reconsignmentControl,
    inventorySelection,
    isFutureDate
  );
  const previousValuesRef = useRef({});
  const [fulfillmentOption, setFulfillmentOption] = useState(selectedOption);

  useEffect(() => {
    if (isFutureDate) {
      setFulfillmentOption(selectedOption);
    } else if (!isFutureDate && parseInt(selectedOption.value.balance) >= parseInt(volume)) {
      setFulfillmentOption(selectedOption);
      // Only call setValue if the values have actually changed
      const newInventorySelection = selectedOption.value.inventorySelection;
      const newReconsignmentControl = selectedOption.value.reconsignmentControl;

      if (previousValuesRef.current.inventorySelection !== newInventorySelection) {
        setValue('inventorySelection', newInventorySelection);
        setValue('reconsignmentControl', newReconsignmentControl);
        previousValuesRef.current.inventorySelection = newInventorySelection;
      }
    } else {
      setFulfillmentOption({});
      previousValuesRef.current = {};
    }
  }, [selectedOption, volume, isFutureDate, setValue]);

  const handleOptionChange = e => {
    const fulfillmentSelection = inventoryOptions.find(
      option => option.value.key === e.target.value
    );

    // setting these values changes our selectedOption, changing our fullfillment option
    setValue('inventorySelection', fulfillmentSelection.value.inventorySelection);
    setValue('reconsignmentControl', fulfillmentSelection.value.reconsignmentControl);
    setValue('setReconsignmentControls', restrictedFlow);
  };

  return (
    <ErrorBoundary>
      <RadioGroup
        onChange={handleOptionChange}
        sx={{ ...radioGroupStyles(theme) }}
        value={fulfillmentOption.key}
      >
        {inventoryOptions
          .sort((a, b) => INVENTORY_SORT_ORDER.indexOf(a.key) - INVENTORY_SORT_ORDER.indexOf(b.key))
          .map(({ value, label }) => (
            <Box
              bgcolor={
                value.key === fulfillmentOption.key
                  ? palette.secondary.light
                  : palette.surface.white
              }
              border={
                value.key === fulfillmentOption.key
                  ? 'none'
                  : `1px solid ${palette.surface.swimlane}`
              }
              key={label}
              sx={{ ...radioButtonStyles(theme) }}
              width="100%"
            >
              <FormControlLabel
                control={<Radio />}
                disabled={
                  !isFutureDate && (!value.balance || parseInt(value.balance) < parseInt(volume))
                }
                label={<RadioLabel isFutureDate={isFutureDate} option={value} />}
                name={`radio-${label.replace(/\s/g, '')}`}
                value={value.key}
              />
            </Box>
          ))}
      </RadioGroup>
    </ErrorBoundary>
  );
};

export default PillsSection;

PillsSection.propTypes = {
  inventoryOptions: arrayOf(shape({})).isRequired,
  inventorySelection: string.isRequired,
  isFutureDate: bool.isRequired,
  reconsignmentControl: string.isRequired,
  restrictedFlow: bool.isRequired,
  setValue: func.isRequired,
  volume: string.isRequired,
};

PillsSection.defaultOptions = {
  isFutureDate: false,
};
